<template>
  <div class="metrics-table">
    <a-table
      :columns="columns"
      :data-source="tableData"
      :loading="loading"
      :pagination="pagination"
      size="middle"
      :rowKey="(record, index) => index"
      @change="handleTableChange"
    >
      <!-- 활동 타입 컬럼 -->
      <template #activityType="{ record }">
        <div class="activity-type">
          <a-tag :color="getActivityColor(metricType)">
            {{ record.displayName || record.type }}
          </a-tag>
        </div>
      </template>

      <!-- 활동 건수 컬럼 -->
      <template #count="{ record }">
        <div class="activity-count">
          <span class="count-value">{{ record.count.toLocaleString() }}</span>
          <span class="count-unit">건</span>
        </div>
      </template>

      <!-- 참여율 컬럼 -->
      <template #rate="{ record }">
        <div class="activity-rate">
          <a-progress
            :percent="parseFloat(record.rate)"
            :size="'small'"
            :strokeColor="getProgressColor(record.rate)"
            :format="percent => `${percent}%`"
          />
        </div>
      </template>

      <!-- 마지막 활동 시간 -->
      <template #lastUsed="{ record }">
        <span>{{ formatDateTime(record.lastUsed) }}</span>
      </template>

      <!-- 액션 버튼 -->
      <template #action="{ record }">
        <a-button type="link" size="small" @click="showDetails(record)">
          상세보기
        </a-button>
      </template>
    </a-table>

    <!-- 활동 상세 드로워 -->
    <a-drawer
      v-model:visible="detailDrawerVisible"
      :title="`'${selectedActivity?.displayName || selectedActivity?.type}' 활동 상세`"
      :width="600"
      placement="right"
    >
      <div v-if="selectedActivity">
        <a-descriptions :column="1" bordered size="small">
          <a-descriptions-item label="활동 타입">
            {{ selectedActivity.displayName || selectedActivity.type }}
          </a-descriptions-item>
          <a-descriptions-item label="총 활동 수">
            {{ selectedActivity.count.toLocaleString() }}건
          </a-descriptions-item>
          <a-descriptions-item label="참여율">
            {{ selectedActivity.rate }}%
          </a-descriptions-item>
          <a-descriptions-item label="마지막 활동">
            {{ formatDateTime(selectedActivity.lastUsed) }}
          </a-descriptions-item>
          <a-descriptions-item label="AARRR 단계">
            <a-tag :color="getActivityColor(metricType)">
              {{ getMetricTitle(metricType) }}
            </a-tag>
          </a-descriptions-item>
        </a-descriptions>

      </div>

      <template #footer>
        <a-button @click="detailDrawerVisible = false">닫기</a-button>
      </template>
    </a-drawer>

  </div>
</template>

<script>
import { ref, computed, watch } from 'vue';
import dayjs from 'dayjs';

export default {
  name: 'MetricsTable',
  props: {
    data: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    metricType: {
      type: String,
      default: ''
    },
    totalAttendees: {
      type: Number,
      default: 0
    }
  },
  setup(props) {
    const detailDrawerVisible = ref(false);
    const selectedActivity = ref(null);

    // 테이블 데이터 가공
    const tableData = computed(() => {
      return props.data.map(item => ({
        ...item,
        rate: props.totalAttendees > 0 ?
          ((item.count / props.totalAttendees) * 100).toFixed(2) : '0'
      }));
    });

    // 페이지네이션 설정
    const pagination = ref({
      current: 1,
      pageSize: 10,
      total: 0,
      showSizeChanger: true,
      showTotal: (total) => `총 ${total}개 활동`,
      pageSizeOptions: ['10', '20', '50']
    });

    // 테이블 컬럼 정의
    const columns = [
      {
        title: '활동 타입',
        dataIndex: 'type',
        key: 'activityType',
        width: '30%',
        slots: { customRender: 'activityType' }
      },
      {
        title: '활동 건수',
        dataIndex: 'count',
        key: 'count',
        width: '20%',
        sorter: (a, b) => a.count - b.count,
        defaultSortOrder: 'descend',
        slots: { customRender: 'count' }
      },
      {
        title: '참여율',
        key: 'rate',
        width: '25%',
        sorter: (a, b) => parseFloat(a.rate) - parseFloat(b.rate),
        slots: { customRender: 'rate' }
      },
      {
        title: '마지막 활동',
        dataIndex: 'lastUsed',
        key: 'lastUsed',
        width: '15%',
        slots: { customRender: 'lastUsed' }
      },
      {
        title: '액션',
        key: 'action',
        width: '10%',
        align: 'center',
        slots: { customRender: 'action' }
      }
    ];


    // 활동 색상 결정
    const getActivityColor = (metricType) => {
      const colorMap = {
        acquisition: 'blue',
        activation: 'green',
        retention: 'orange',
        revenue: 'purple',
        referral: 'red'
      };
      return colorMap[metricType] || 'default';
    };

    // 진행률 색상 결정
    const getProgressColor = (rate) => {
      const rateValue = parseFloat(rate);
      if (rateValue >= 50) return '#52c41a';
      if (rateValue >= 20) return '#faad14';
      return '#ff4d4f';
    };

    // AARRR 단계 제목
    const getMetricTitle = (key) => {
      const titles = {
        acquisition: '획득',
        activation: '활성화',
        retention: '유지',
        revenue: '수익',
        referral: '추천'
      };
      return titles[key] || '전체';
    };

    // 날짜 포맷
    const formatDateTime = (date) => {
      return date ? dayjs(date).format('YYYY-MM-DD HH:mm') : '-';
    };

    // 테이블 변경 처리
    const handleTableChange = (pag) => {
      pagination.value = { ...pagination.value, ...pag };
    };

    // 상세보기
    const showDetails = async (record) => {
      selectedActivity.value = record;
      detailDrawerVisible.value = true;
    };




    // 데이터 변경 감지
    watch(() => props.data, () => {
      pagination.value.total = props.data.length;
    });

    return {
      tableData,
      columns,
      pagination,
      detailDrawerVisible,
      selectedActivity,
      getActivityColor,
      getProgressColor,
      getMetricTitle,
      formatDateTime,
      handleTableChange,
      showDetails
    };
  }
};
</script>

<style scoped>
.metrics-table {
  padding: 0;
}

.activity-type {
  display: flex;
  align-items: center;
}

.activity-count {
  display: flex;
  align-items: baseline;
  gap: 4px;
}

.count-value {
  font-size: 16px;
  font-weight: 600;
  color: #1890ff;
}

.count-unit {
  font-size: 12px;
  color: #666;
}

.activity-rate {
  min-width: 120px;
}

:deep(.ant-progress-text) {
  font-size: 12px;
}

:deep(.ant-table-thead > tr > th) {
  background: #fafafa;
  font-weight: 600;
}

:deep(.ant-drawer-body) {
  padding: 16px;
}

:deep(.ant-descriptions-item-label) {
  font-weight: 500;
}
</style>